import type { Frame } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";

export interface ConversationMetadataArgs {
  conversationId?: string;
  runtimeSessionId?: string;
  executionId?: string;
  projectAgentId?: string;
  agentKey?: string;
  agentName?: string;
  sessionModel?: string | null;
  sessionAdapterType?: string | null;
}

export function frameTitle(agentName?: string): string {
  return `Chat with ${agentName || "Agent"}`;
}

export function frameMatchesConversation(
  frame: Frame,
  args: ConversationMetadataArgs & { frameId?: string },
): boolean {
  if (frame.frame_type !== "conversation") return false;
  if (args.frameId && frame.id === args.frameId) return true;
  return metadataMatchesConversation(frame.metadata ?? {}, args);
}

export function metadataMatchesConversation(
  metadata: Record<string, unknown>,
  args: ConversationMetadataArgs,
): boolean {
  if (args.conversationId && metadata.conversationId === args.conversationId) {
    return true;
  }
  if (args.conversationId) return false;
  if (
    metadataValue(metadata, "conversationId") ||
    metadataValue(metadata, "conversation_id")
  ) {
    return false;
  }
  return Boolean(
    (args.projectAgentId &&
      (metadata.projectAgentId === args.projectAgentId ||
        metadata.project_agent_id === args.projectAgentId)) ||
    (args.agentKey &&
      (metadata.agentKey === args.agentKey ||
        metadata.agent_key === args.agentKey)),
  );
}

export function metadataValue(
  metadata: Record<string, unknown>,
  key: string,
): string | null {
  const value = metadata[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function metadataNeedsConversationBinding(
  metadata: Record<string, unknown>,
  args: ConversationMetadataArgs,
): boolean {
  return Boolean(
    (args.conversationId && metadata.conversationId !== args.conversationId) ||
    (args.runtimeSessionId &&
      metadata.runtimeSessionId !== args.runtimeSessionId) ||
    (args.executionId && metadata.executionId !== args.executionId) ||
    (args.projectAgentId && metadata.projectAgentId !== args.projectAgentId) ||
    (args.agentKey && metadata.agentKey !== args.agentKey) ||
    (args.agentName && metadata.agentName !== args.agentName) ||
    (args.sessionModel && metadata.sessionModel !== args.sessionModel) ||
    (args.sessionAdapterType &&
      metadata.sessionAdapterType !== args.sessionAdapterType),
  );
}

export function conversationFrameMetadata(
  base: Record<string, unknown> | undefined,
  args: ConversationMetadataArgs,
): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    ...base,
    conversationFrame: true,
  };
  if (args.conversationId) {
    delete metadata.conversationId;
    delete metadata.conversation_id;
    delete metadata.executionId;
    delete metadata.execution_id;
    delete metadata.runtimeSessionId;
    delete metadata.liveSessionId;
  }
  delete metadata.sessionId;
  delete metadata.session_id;
  if (args.runtimeSessionId) {
    delete metadata.runtimeSessionId;
    delete metadata.liveSessionId;
  }
  if (args.executionId) delete metadata.execution_id;
  return {
    ...metadata,
    ...(args.conversationId ? { conversationId: args.conversationId } : {}),
    ...(args.runtimeSessionId
      ? { runtimeSessionId: args.runtimeSessionId }
      : {}),
    ...(args.executionId ? { executionId: args.executionId } : {}),
    ...(args.projectAgentId ? { projectAgentId: args.projectAgentId } : {}),
    ...(args.agentKey ? { agentKey: args.agentKey } : {}),
    ...(args.agentName ? { agentName: args.agentName } : {}),
    ...(args.sessionModel ? { sessionModel: args.sessionModel } : {}),
    ...(args.sessionAdapterType
      ? { sessionAdapterType: args.sessionAdapterType }
      : {}),
  };
}

/** A conversation frame's local copy as an API frame, drawn in `box`. */
export function frameDataToConversationFrame(
  data: FrameData,
  box: { x: number; y: number; width: number; height: number },
): Frame {
  return {
    id: data.frameId,
    space: "",
    frame_type: data.frameType,
    title: data.title,
    content: data.content,
    status: data.status,
    position_x: box.x,
    position_y: box.y,
    width: box.width,
    height: box.height,
    agent_id: data.agentId,
    parent_frame: data.parentFrame,
    metadata: data.metadata,
    created_at: data.createdAt,
    updated_at: data.createdAt,
    task_ticket_number: null,
    task_id: null,
    task_title: null,
    edited_by_user_at: data.editedByUserAt,
  };
}
